// TODO: Remove is disabled rule

/* eslint-disable @typescript-eslint/no-explicit-any */
import {SupabaseInstance} from './supabaseClass';

class DatabaseClass {
  // Use the same exact type as what SupabaseInstance.supabase is using
  private supabase: typeof SupabaseInstance.supabase;

  constructor() {
    this.supabase = SupabaseInstance.supabase;
  }

  /**
   * Inserts or updates a record in the specified table.
   * If `initial` is true, it first checks if a record exists based on the condition.
   * If a record exists, it returns the existing record without inserting or updating.
   * Otherwise, it performs an upsert operation to insert or update the record.
   *
   * @param {Object} params - The parameters for setting the record.
   * @param {string} params.table - The name of the table where the record will be set.
   * @param {any} params.data - The data to be inserted or updated.
   * @param {boolean} [params.initial] - Whether to check for existing records first.
   * @param {Object} [params.condition] - The condition used to fetch existing records if `initial` is true.
   * @param {string} params.condition.key - The key to check for existing records.
   * @param {any} params.condition.value - The value to check for existing records.
   * @returns {Promise<any>} - The inserted or updated record, or the existing record if `initial` is true.
   * @throws {Error} - If an error occurs during the database operation.
   */
  public setRecord = async ({
    table,
    data,
    initial,
    condition,
    insertCondition,
  }: {
    table: string;
    data: any;
    initial?: boolean;
    condition?: any;
    insertCondition?: any;
  }) => {
    const newData = {
      ...data,
      date_modified: new Date(),
    };
    if (initial) {
      const {data: records, error} = await this.supabase.from(table).select('*').eq(condition.key, condition.value);
      if (error) {
        throw new Error(error.message);
      }
      if (records.length > 0) {
        return records[0];
      }
    }

    if (insertCondition) {
      const {data: record, error} = await this.supabase
        .from(table)
        .upsert(newData)
        .eq(insertCondition.key, insertCondition.value)
        .select();
      if (error) {
        throw new Error(error.message);
      }

      return record;
    }

    const {data: record, error} = await this.supabase.from(table).upsert(newData).select();

    if (error) {
      throw new Error(`error in set record, ${error.message}`);
    }

    return record[0];
  };

  /**
   * Updates a record in the specified table based on the condition.
   *
   * @param {Object} params - The parameters for updating a record.
   * @param {string} params.table - The name of the table to update the record in.
   * @param {any} params.data - The data to update the record with.
   * @param {any} params.condition - The condition used to update the record.
   * @param {string} params.condition.key - The key to check for the record to update.
   * @param {any} params.condition.value - The value to check for the record to update.
   * @returns {Promise<any>} - The updated record.
   * @throws {Error} - If an error occurs during the database operation.
   * */
  public updateRecord = async ({table, data, condition}: {table: string; data: any; condition: any}) => {
    try {
      const {data: sessionData} = await SupabaseInstance.getCurrentSession();

      const updatedData = {
        ...data,
        modified_by: sessionData.session?.user.id,
        date_modified: new Date(),
      };

      const {data: record, error} = await this.supabase
        .from(table)
        .update(updatedData)
        .eq(condition.key, condition.value)
        .select();

      if (error || !record) {
        console.error('error in update record', error.message);
        return;
      }

      return record[0];
    } catch (error) {
      console.error('error in update record', error);
      throw new Error((error as Error).message);
    }
  };

  /**
   * Deletes a record from the specified table based on the condition.
   *
   * @param {Object} params - The parameters for deleting a record.
   * @param {string} params.table - The name of the table to delete the record from.
   * @param {any} params.condition - The condition used to delete the record.
   * @param {string} params.condition.key - The key to check for the record to delete.
   * @param {any} params.condition.value - The value to check for the record to delete.
   * @param {any} [params.condition2] - The second condition used to delete the record.
   * @param {string} params.condition2.key - The key to check for the record to delete.
   * @param {any} params.condition2.value - The value to check for the record to delete.
   * @returns {Promise<void>} - The result of the delete operation.
   * @throws {Error} - If an error occurs during the database operation.
   */
  public deleteRecord = async ({
    table,
    condition,
    condition2,
    hardDelete,
  }: {
    table: string;
    condition: {key: string; value: any};
    condition2?: {key: string; value: any};
    hardDelete?: boolean;
  }) => {
    const {data: sessionData, error: sessionErr} = await SupabaseInstance.getCurrentSession();

    if (sessionErr) {
      console.error('error in delete record', sessionErr.message);
      throw new Error(sessionErr.message);
    }

    let query = this.supabase
      .from(table)
      .update({
        date_removed: new Date().toISOString(),
        removed_by: sessionData.session?.user.id,
      })
      .eq(condition.key, condition.value);

    // Add the second condition if it exists
    if (condition2) {
      query = query.eq(condition2.key, condition2.value);
    }

    // Perform a hard delete if specified
    if (hardDelete) {
      query = this.supabase.from(table).delete().eq(condition.key, condition.value);
    }

    const {data: record, error} = await query.select();

    if (error) {
      console.error('error in delete record', error.message);
    }

    return record;
  };

  /**
   * Fetches records from the specified table based on the condition.
   * It also allows for filtering, ordering, and limiting the fetched records.
   *
   * @param {Object} params - The parameters for fetching records.
   * @param {string} params.table - The name of the table to fetch records from.
   * @param {any} params.filters - The filters used to fetch records.
   * @param {any} params.order - The order used to fetch records.
   * @param {number} params.limit - The limit used to fetch records.
   * @param {Function} params.callback - The callback function to process the fetched records.
   * @returns {Promise<any>} - The processed records.
   * @throws {Error} - If an error occurs during the database operation.
   * */
  public complexQuery = async ({
    table,
    filters,
    order,
    limit,
    callback,
  }: {
    table: string;
    filters: any;
    order: any;
    limit: number;
    callback: any;
  }) => {
    // initialize the query
    let query = this.supabase.from(table).select('*');

    // add filters, order, and limit to the query
    if (filters) {
      query = query.eq(filters.key, filters.value);
    }
    if (order) {
      query = query.order(order.key, {ascending: order.ascending});
    }
    if (limit) {
      query = query.limit(limit);
    }
    const {data, error} = await query;
    if (error) {
      throw new Error(error.message);
    }
    return callback(data);
  };
}

const DatabaseInstance = new DatabaseClass();

export {DatabaseInstance};
